import { createTheme } from '@mui/material/styles';

/**
 * THEMING SYSTEM ARCHITECTURE
 * 
 * This file is organized in layers:
 * 1. Base palette - Raw color values
 * 2. Semantic tokens - Purpose-based naming of colors
 * 3. Component tokens - Component-specific theming
 * 4. Theme construction - Light and dark theme assembly
 * 5. Helper functions - Utilities for working with theme colors
 */

/**
 * @typedef {Object} CustomPaletteColorOptions
 * @property {string} iconPrimary - Primary icon color
 * @property {string} iconSecondary - Secondary icon color
 * @property {string} iconDisabled - Disabled icon color
 * @property {string} borderColor - Border color
 * @property {string} bannerBg - Banner background color
 * @property {string} hoverRowBg - Hover row background color
 * @property {string} cardBackground - Card background color
 * @property {string} tooltipBackground - Tooltip background color
 * @property {string} tooltipText - Tooltip text color
 * @property {string} titleText - Title text color
 * @property {string} navBg - Navigation background color
 * @property {string} navHoverBg - Navigation hover background color
 * @property {string} navSelectedText - Navigation selected text color
 * @property {string} navText - Navigation text color
 * @property {string} tickerBannerBg - Ticker banner background color
 * @property {string} tickerBannerText - Ticker title text color
 * @property {string} tickerBannerHoverText - Ticker banner hover text color
 */

// ========================================
// 1. BASE PALETTE - Raw color definitions
// ========================================
const baseColors = {
  // Brand colors
  white: '#FFFFFF',
  navy: '#101A36',
  purple: '#32127A',
  blue: '#6495ED',
  lightPurple: '#CCCCFF',
  orange: '#FF8200',
  
  // Grayscale
  gray100: '#F5F7FA',
  gray200: '#F0F4FF',
  gray300: '#E0E0E0',
  gray400: '#DDE3EA',
  gray500: '#999999',
  gray600: '#314158',
  gray700: '#2A2A2A',
  gray800: '#182235',
  gray900: '#1C2541',
  
  // Status colors
  green: '#00C853',
  lime: '#98CD9A',
  yellow: '#FFC107',
  salmon: '#CD6163',
  red: '#D50000',
  
  // Misc UI colors - disabled UI elements colors
  disabledBg: '#FFFFFF',
  disabledText: '#2A2A2A',
  disabledIcon: '#2A2A2A',
};

// ========================================
// 2. SEMANTIC TOKENS - Purpose-based color assignments
// ========================================
const semanticColors = {
  // Signal semantic colors
  strongBuy: baseColors.green,
  buy: baseColors.lime,
  hold: baseColors.yellow,
  sell: baseColors.salmon,
  strongSell: baseColors.red,

  // Performance indicators
  gain: baseColors.green,
  flat: baseColors.yellow,
  loss: baseColors.red,

  // Trend indicators
  bullish: baseColors.green,
  modBullish: baseColors.lime,
  neutral: baseColors.yellow,
  modBearish: baseColors.salmon,
  bearish: baseColors.red,

  // Change indicators
  improving: baseColors.green,
  steady: baseColors.yellow,
  deteriorating: baseColors.red,

  // Risk indicators
  lowRisk: baseColors.green,
  medLowRisk: baseColors.lime,
  medHighRisk: baseColors.salmon,
  highRisk: baseColors.red,

  // Flow indicators
  inflow: baseColors.green,
  outflow: baseColors.red,

  // Recommendation indicators
  invest: baseColors.green,
  consider: baseColors.yellow,
  reduce: baseColors.red,

  // Market Regime indicators
  bullMarketEarlyStrong: baseColors.green, //strongBuy
  bullMarketMatureLate: baseColors.lime, //buy
  marketTopTransition: baseColors.yellow, //flat
  rangeBoundConsolidation: baseColors.yellow, //flat
  marketBottomRecovery: baseColors.yellow, //flat
  bearMarketEarly: baseColors.salmon, //sell
  bearMarketMatureDeep: baseColors.red, //strongSell

  // Add new UI-specific semantic colors
  bannerBg: baseColors.blue,          // Banner/card background color
  shadowColor: baseColors.navy,       // Shadow color for elements
  //tooltipBackground: baseColors.navy,  // Background for tooltips
  //tooltipText: baseColors.white,      // Text color for tooltips
  // Add any other UI-specific semantic colors you need
};

// ========================================
// 3. UI TOKENS - Mode-specific UI assignments
// ========================================
const lightModeUI = {
  // Surfaces
  background: baseColors.white,
  cardBg: baseColors.gray100,
  hoverRowBg: baseColors.gray200,
  
  // Borders and dividers
  border: baseColors.gray400,
  divider: baseColors.gray300,
  
  // Text
  primaryText: baseColors.purple,
  secondaryText: baseColors.navy,
  
  // Accents
  bannerBg: baseColors.blue,
  titleText: baseColors.purple,
  tickerBannerBg: baseColors.gray100,
  tickerBannerText: baseColors.navy,
  tickerBannerHoverText: baseColors.blue,

  // Tooltips
  //tooltipBg: baseColors.blue,
  //tooltipText: baseColors.white,

  //Icons
  //iconBg: baseColors.white,
  //iconText: baseColors.navy,
  iconPrimary: baseColors.purple,
  iconSecondary: baseColors.blue,
  iconDisabled: baseColors.gray400,

  // Navigation
  navBg: baseColors.blue,
  navHoverBg: baseColors.lightPurple,
  navSelectedText: baseColors.purple,
  navText: baseColors.purple,
  
  // Additional UI tokens
  surfaceElevated: baseColors.white,
  shadowLight: baseColors.navy,
  shadowColor: baseColors.navy, // Add shadowColor for light mode
  //focusRing: baseColors.blue,
  focusRing: baseColors.purple,
  inputBorder: baseColors.gray400,
  //inputFocus: baseColors.blue,
  inputFocus: baseColors.purple,
};

const darkModeUI = {
  // Surfaces
  background: baseColors.navy,
  cardBg: baseColors.gray900,
  hoverRowBg: baseColors.gray800,
  
  // Borders and dividers
  border: baseColors.gray600,
  divider: baseColors.gray700,
  
  // Text
  primaryText: baseColors.white,
  secondaryText: baseColors.lightPurple,
  
  // Accents
  bannerBg: baseColors.blue,
  titleText: baseColors.white,
  tickerBannerBg: baseColors.navy,
  tickerBannerText: baseColors.white,
  tickerBannerHoverText: baseColors.blue,
  
  // Tooltips
  tooltipBg: baseColors.blue,
  tooltipText: baseColors.white,
  // tooltipBg: baseColors.white,
  // tooltipText: baseColors.navy,

  //Icons
  iconPrimary: baseColors.white,
  iconSecondary: baseColors.blue,
  iconDisabled: baseColors.gray400,

  // Navigation - Update to use proper dark mode colors
  navBg: baseColors.blue,
  navHoverBg: baseColors.lightPurple,
  navSelectedText: baseColors.white,
  navText: baseColors.white,
  
  // Additional UI tokens
  surfaceElevated: baseColors.gray800,
  shadowLight: baseColors.navy,
  shadowColor: baseColors.navy, // Add shadowColor for dark mode
  focusRing: baseColors.purple,
  inputBorder: baseColors.gray600,
  inputFocus: baseColors.purple,
  
  // Fix missing properties
  disabledBg: baseColors.gray700,
};

// ========================================
// 4. COMMON THEME SETTINGS - Shared between modes
// ========================================
const commonSettings = {
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: '32px' },
    h2: { fontWeight: 600, fontSize: '24px' },
    body1: { fontWeight: 400, fontSize: '16px' },
    body2: { fontWeight: 400, fontSize: '14px' },
    caption: { fontWeight: 400, fontSize: '12px' },
    button: { fontWeight: 600, fontSize: '14px', textTransform: 'none' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, padding: '8px 16px', minHeight: '44px', color: baseColors.orange },
        containedPrimary: {
          '&:hover': { bgcolor: baseColors.lightPurple }, 
        },
        containedSecondary: {
          '&:hover': { bgcolor: baseColors.purple }, 
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { color: baseColors.orange },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 600,
        },
      },
    },
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
};

// ========================================
// 5. THEME CONSTRUCTION - Final theme objects
// ========================================

// Light theme
const lightTheme = createTheme({
  ...commonSettings,
  palette: {
    mode: 'light',
    primary: {
      main: baseColors.blue,
      contrastText: baseColors.navy,
    },
    secondary: {
      main: baseColors.purple,
      contrastText: baseColors.white,
    },
    background: {
      default: lightModeUI.background,
      paper: lightModeUI.cardBg,
    },
    text: {
      primary: lightModeUI.primaryText,
      secondary: lightModeUI.secondaryText,
    },
    action: {
      disabled: baseColors.disabledText,
      disabledBackground: baseColors.disabledBg,
    },
    divider: lightModeUI.divider,
    info: {
      main: baseColors.blue,
    },
    // Custom properties for use throughout the app
    custom: {
      // Include all base colors for reference
      ...baseColors,
      // Include all semantic colors
      ...semanticColors,
      // Include mode-specific UI colors
      cardBackground: lightModeUI.cardBg,
      tooltipBackground: lightModeUI.background,
      tooltipText: lightModeUI.primaryText,
      hoverRowBg: lightModeUI.hoverRowBg,
      borderColor: lightModeUI.border,
      bannerBg: lightModeUI.bannerBg,
      titleText: lightModeUI.titleText,
      // Icon colors
      iconPrimary: lightModeUI.iconPrimary,
      iconSecondary: lightModeUI.iconSecondary,
      //iconSecondary: baseColors.orange,
      iconDisabled: lightModeUI.iconDisabled,              
      // Navigation colors
      navBg: lightModeUI.navBg,
      navHoverBg: lightModeUI.navHoverBg,
      navSelectedText: lightModeUI.navSelectedText,
      navText: lightModeUI.navText,
      // Ticker colors
      tickerBannerBg: lightModeUI.tickerBannerBg,
      tickerBannerText: lightModeUI.tickerBannerText,
      tickerBannerHoverText: lightModeUI.tickerBannerHoverText,
      // Additional UI colors
      surfaceElevated: lightModeUI.surfaceElevated,
      shadowLight: lightModeUI.shadowLight,
      shadowColor: lightModeUI.shadowColor, // Add shadowColor
      focusRing: lightModeUI.focusRing,
      inputBorder: lightModeUI.inputBorder,
      inputFocus: lightModeUI.inputFocus,

      // SHOULD ADD BUTTON COLORS *************************************
    },
  },
});

// Dark theme
const darkTheme = createTheme({
  ...commonSettings,
  palette: {
    mode: 'dark',
    primary: {
      main: baseColors.blue,
      contrastText: baseColors.navy,
    },
    secondary: {
      main: baseColors.lightPurple,
      contrastText: baseColors.white,
    },
    background: {
      default: darkModeUI.background,
      paper: darkModeUI.cardBg,
    },
    text: {
      primary: darkModeUI.primaryText,
      secondary: darkModeUI.secondaryText,
    },
    action: {
      disabled: baseColors.disabledText,
      disabledBackground: darkModeUI.disabledBg,
      //disabledIcon: baseColors.disabledIcon,                     // REMOVE DUPLICATE
    },
    divider: darkModeUI.divider,
    info: {
      main: baseColors.blue,
    },
    // Custom properties for use throughout the app
    custom: {
      // Include all base colors for reference
      ...baseColors,
      // Include all semantic colors
      ...semanticColors,
      // Include mode-specific UI colors
      cardBackground: darkModeUI.cardBg,
      tooltipBackground: darkModeUI.tooltipBg,
      tooltipText: darkModeUI.tooltipText,
      hoverRowBg: darkModeUI.hoverRowBg,
      borderColor: darkModeUI.border,
      bannerBg: darkModeUI.bannerBg,
      titleText: darkModeUI.titleText,
      // Icon colors
      iconPrimary: darkModeUI.iconPrimary,
      iconSecondary: darkModeUI.iconSecondary,
      //iconSecondary: baseColors.orange,
      iconDisabled: darkModeUI.iconDisabled,
      // Navigation colors
      navBg: darkModeUI.navBg,
      navHoverBg: darkModeUI.navHoverBg,
      navSelectedText: darkModeUI.navSelectedText,
      navText: darkModeUI.navText,
      // Ticker colors
      tickerBannerBg: darkModeUI.tickerBannerBg,
      tickerBannerText: darkModeUI.tickerBannerText,
      tickerBannerHoverText: darkModeUI.tickerBannerHoverText,
      // Additional UI colors
      surfaceElevated: darkModeUI.surfaceElevated,
      shadowLight: darkModeUI.shadowLight,
      shadowColor: darkModeUI.shadowColor, // Add shadowColor
      focusRing: darkModeUI.focusRing,
      inputBorder: darkModeUI.inputBorder,
      inputFocus: darkModeUI.inputFocus,
    },
  },
});

// ========================================
// 6. HELPER FUNCTIONS - Utilities for working with colors
// ========================================

/**
 * Maps signal labels to their corresponding colors
 * Used for consistent color representation of recommendations
 */
const signalColors = {
  'STRONGBUY': semanticColors.strongBuy,
  'BUY': semanticColors.buy,
  'HOLD': semanticColors.hold,
  'SELL': semanticColors.sell,
  'STRONGSELL': semanticColors.strongSell,
  // Performance
  'GAIN': semanticColors.gain,
  'FLAT': semanticColors.flat,
  'LOSS': semanticColors.loss,
  // Trend
  'BEARISH': semanticColors.bearish,
  'MODERATELYBEARISH': semanticColors.modBearish,
  'NEUTRAL': semanticColors.neutral,
  'MODERATELYBULLISH': semanticColors.modBullish,
  'BULLISH': semanticColors.bullish,
  // Change
  'IMPROVING': semanticColors.improving,
  'STEADY': semanticColors.steady,
  'DETERIORATING': semanticColors.deteriorating,
  // Risk
  'HIGH': semanticColors.highRisk,
  'MEDIUMHIGH': semanticColors.medHighRisk,
  'MEDIUMLOW': semanticColors.medLowRisk,
  'LOW': semanticColors.lowRisk,
  // Flow
  'INFLOW': semanticColors.inflow,
  'OUTFLOW': semanticColors.outflow,
  // Recommendation
  'INVEST': semanticColors.invest,
  'CONSIDER': semanticColors.consider,
  'REDUCE': semanticColors.reduce,

  // Market Regime
  'BULLMARKETEARLYSTRONGPHASE': semanticColors.bullMarketEarlyStrong,
  'BULLMARKETEARLYPHASE': semanticColors.bullMarketEarlyStrong,
  'BULLMARKETMATURELATEPHASE': semanticColors.bullMarketMatureLate,
  'BULLMARKETLATEPHASE': semanticColors.bullMarketMatureLate,
  'MARKETTOPTRANSITION': semanticColors.marketTopTransition,
  'BEARMARKETEARLYPHASE': semanticColors.bearMarketEarly,
  'BEARMARKETMATUREDEEPPHASE': semanticColors.bearMarketMatureDeep, 
  'BEARMARKETDEEPPHASE': semanticColors.bearMarketMatureDeep, 
  'MARKETBOTTOMRECOVERY': semanticColors.marketBottomRecovery,
  'RANGEBOUNDCONSOLIDATION': semanticColors.marketTopTransition,
};

/**
 * Returns the appropriate color for a given signal label
 * @param {string} label - The signal label to get color for
 * @returns {string} The color associated with the label
 */
const getSignalColor = (label) => {
  if (!label) return semanticColors.hold;
  
  // Remove spaces, hyphens, forward slashes and convert to uppercase
  const normalizedLabel = label.replace(/[()\s\-/]/g, '').toUpperCase();
  return signalColors[normalizedLabel] || semanticColors.hold;
};

/**
 * Returns a color based on numeric value trend
 * @param {number} value - The numeric value to evaluate
 * @param {object} theme - The current theme object
 * @returns {string} The appropriate color for the trend
 */
const getTrendColor = (value, theme) => {
  if (value > 0) return semanticColors.strongBuy;
  if (value < 0) return semanticColors.strongSell;
  return theme.palette.text.secondary;
};

/**
 * Responsive font size definitions for different screen sizes
 */
const responsiveFontSizes = {
  desktop: {
    h1: '32px',
    h2: '24px',
    body1: '16px',
    body2: '14px',
    caption: '12px',
  },
  mobile: {
    h1: '28px',
    h2: '20px',
    body1: '14px',
    body2: '13px',
    caption: '11px',
  },
};

export {
  lightTheme,
  darkTheme,
  baseColors,
  semanticColors,
  getSignalColor,
  getTrendColor,
  responsiveFontSizes,
};